import { defineComponent as _defineComponent } from 'vue'
import {
  ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount,
} from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import DurationRulesDialog from '@/components/views/settings/DurationRulesDialog.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import TablesDialog from '@/components/views/settings/TablesDialog.vue';
import FloorplanEditDialog from '@/components/views/settings/FloorplanEditDialog.vue';
import TabAllocationsDialog, { AllocationsDialogMode } from '@/components/views/settings/TabAllocationsDialog.vue';
import Tab from '@/model/Tab';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import { performSaveAction, performAction } from '@/services/vue-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import AllocationsStore from '@/store/allocations-store';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import useStoreWatch from '@/composables/store-watch';
import { useRoute, useRouter } from 'vue-router/composables';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import useUnsavedGuard from '@/composables/unsaved-guard';
import { ValidationObserver } from 'vee-validate';
import { useSeatingTimeItems, useBookingCaps } from './settings/helpers/settings-values';


export default /*#__PURE__*/_defineComponent({
  __name: 'Section',
  setup(__props) {

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const router = useRouter();
const route = useRoute();

const section = ref(new Tab());
const oldSection = ref(new Tab());

const seatingTimes = useSeatingTimeItems();
const bookingCaps = useBookingCaps();
const minSeatingTimes = computed(() => {
  const st = seatingTimes.value.filter((o) => o.value <= (section.value.duration ?? 0));
  const mst = [{ value: 0, text: i18n.tc('label.default_seating_time') }, ...st];
  return mst;
});

// load data
const reset = () => {
  const id = Number(router.currentRoute.params.id) || 0;
  const s = store.state.settings.tabs.find((o) => o.id === id) ?? new Tab();
  section.value = cloneModel(s);
  oldSection.value = s;
};
useStoreWatch(reset, true);

const isNew = computed(() => !section.value.id);
const isChanged = computed(() => !isModelEqualToModel(section.value, oldSection.value));
useUnsavedGuard(isChanged);

const loadAllocations = () => store.dispatch('loadAllocations');
const resetAllocations = () => store.dispatch('resetAllocations');

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'allocations', AllocationsStore);
});
onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'allocations', async () => { resetAllocations(); });
});

// Dialogs
const deleteConfirmation = ref(false);
const tablesDialog = ref(false);
const floorplanDialog = ref(false);
const allocationsDialog = ref(false);
const allocationsDialogMode = ref(AllocationsDialogMode.AllowedAllocations);
const durationRulesDialog = ref(false);

const showAllocationsDialog = async (mode: AllocationsDialogMode) => {
  allocationsDialogMode.value = mode;
  const ok = await performAction(
    null,
    i18n.tc('error.load_allocations'),
    async () => store.dispatch('loadAllocations', { tabId: section.value.id }),
  );
  if (ok) allocationsDialog.value = true;
};

const showAllowedAllocationsDialog = async () => {
  showAllocationsDialog(AllocationsDialogMode.AllowedAllocations);
};

const showDisallowedAllocationsDialog = async () => {
  showAllocationsDialog(AllocationsDialogMode.DisallowedAllocations);
};

const closeDialogs = () => {
  tablesDialog.value = false;
  floorplanDialog.value = false;
  reset();
};

watch(() => route.name, () => {
  if (route.query.floorplan) {
    floorplanDialog.value = true;
  } else if (route.query.tables) {
    tablesDialog.value = true;
  }
});

// slider
const maxCapacity = computed(() => section.value.tabItems.map((o) => o.seats).reduce((a, b) => a + b, 0));
const maxCapacitySlider = computed(() => (maxCapacity.value === 0 ? 100 : maxCapacity.value));

const updateCapacity = (onlineValue: boolean) => {
  if (!onlineValue) return;
  section.value.capacity = maxCapacity.value;
};

const changeSeatingTime = (value: number) => {
  section.value.duration = value;
  if (section.value.minDuration >= value) {
    section.value.minDuration = 0;
  }
};

// save delete
const save = async () => {
  const entities = store.state.settings.tabs.map(
    (o) => (o.id === section.value.id ? section.value : cloneModel(o)),
  );
  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendTabs', { entities }),
  );

  if (ok) {
    reset();
    router.back();
  }
};

const remove = async () => {
  const entities = store.state.settings.tabs.map((o) => cloneModel(o));
  const index = entities.findIndex((o: Tab) => o.id === section.value.id);
  if (index >= 0) entities.splice(index, 1);
  const ok = await performAction(
    i18n.tc('message.success_section_deleted'),
    i18n.tc('error.deleting_section'),
    async () => store.dispatch('sendTabs', { entities }),
  );
  if (ok) {
    reset();
    router.back();
  }
};


return { __sfc: true,observerRef, router, route, section, oldSection, seatingTimes, bookingCaps, minSeatingTimes, reset, isNew, isChanged, loadAllocations, resetAllocations, deleteConfirmation, tablesDialog, floorplanDialog, allocationsDialog, allocationsDialogMode, durationRulesDialog, showAllocationsDialog, showAllowedAllocationsDialog, showDisallowedAllocationsDialog, closeDialogs, maxCapacity, maxCapacitySlider, updateCapacity, changeSeatingTime, save, remove, useSettingsBreadcrumbs, DurationRulesDialog, ConfirmationDialog, TablesDialog, FloorplanEditDialog, TabAllocationsDialog, SettingsForm, SettingsGroup, ValidationObserver }
}

})